import {connect} from 'react-redux';

import isEqual from '@tinkoff/utils/is/equal';

import {createSelector} from 'reselect';

import {getPost, makeGetPostsForIds} from 'mattermost-redux/selectors/entities/posts';
import {getChannel, getDirectTeammate} from 'mattermost-redux/selectors/entities/channels';
import {getCurrentUserId, getCurrentUser} from 'mattermost-redux/selectors/entities/common';

import type {Channel} from 'mattermost-redux/types/channels';
import type {Post} from 'mattermost-redux/types/posts';

import {type FakePost} from 'types/store/rhs';

import type {GlobalState} from 'types/store';

import {getLastViewedAtByThreadId} from 'features/threads/selectors/get_last_viewed_at_by_thread_id';

import {isTimezoneEnabled} from 'mattermost-redux/selectors/entities/timezone';

import {
    DATE_LINE,
    makeCombineUserActivityPosts,
    START_OF_NEW_MESSAGES,
} from 'mattermost-redux/utils/post_list';
import {createIdsSelector} from 'mattermost-redux/utils/helpers';
import {getUserCurrentTimezone} from 'mattermost-redux/utils/timezone_utils';

import {getTimezoneOffset} from 'utils/datetime';
import {isFromWebhook} from 'utils/post_utils';

import {getThreadPostFollowState} from 'features/threads/selectors/get_thread_post_follow_state';

import ThreadViewerVirtualized from './virtualized_thread_viewer';

type OwnProps = {
    channelId: Channel['id'];
    postIds: Array<Post['id'] | FakePost['id']>;
    selected: Post | FakePost;
    useRelativeTimestamp: boolean;
};

interface PostFilterOptions {
    postIds: Array<Post['id']>;
    showDate: boolean;
    lastViewedAt?: number;
}

/**
 * Returns a selector that, given the state and an object containing an array of postIds and an optional
 * timestamp of when the channel was last read, returns a memoized array of postIds interspersed with
 * day indicators, an optional new message indicator and create comment.
 *
 * @TODO: перенесено из старого кода. Нужно переработать
 */
function makeFilterRepliesAndAddSeparators() {
    const getPostsForIds = makeGetPostsForIds();

    return createIdsSelector(
        (state: GlobalState, {postIds}: PostFilterOptions) => getPostsForIds(state, postIds),
        (_state: GlobalState, {lastViewedAt}: PostFilterOptions) => lastViewedAt,
        (_state: GlobalState, {showDate}: PostFilterOptions) => showDate,
        getCurrentUser,
        isTimezoneEnabled,
        (posts, lastViewedAt, showDate, currentUser, timeZoneEnabled) => {
            if (posts.length === 0 || !currentUser) {
                return [];
            }

            const out: string[] = [];
            let lastDate;
            let addedNewMessagesIndicator = false;

            // Iterating through the posts from oldest to newest
            for (let i = posts.length - 1; i >= 0; i--) {
                const post = posts[i];

                if (!post) {
                    continue;
                }

                if (showDate) {
                    // Push on a date header if the last post was on a different day than the current one
                    const postDate = new Date(post.create_at);

                    if (timeZoneEnabled) {
                        const timezone = getUserCurrentTimezone(currentUser.timezone);

                        if (timezone) {
                            const currentOffset = postDate.getTimezoneOffset() * 60 * 1000;
                            const timezoneOffset = getTimezoneOffset(timezone, post.create_at);
                            postDate.setTime(post.create_at + currentOffset + timezoneOffset);
                        }
                    }

                    if (!lastDate || lastDate.toDateString() !== postDate.toDateString()) {
                        out.push(DATE_LINE + post.create_at);
                        lastDate = postDate;
                    }
                }

                if (
                    typeof lastViewedAt === 'number' &&
                    post.create_at >= lastViewedAt &&
                    i < posts.length - 1 &&
                    (post.user_id !== currentUser.id || isFromWebhook(post)) &&
                    !addedNewMessagesIndicator
                ) {
                    out.push(START_OF_NEW_MESSAGES);
                    addedNewMessagesIndicator = true;
                }

                out.push(post.id);
            }

            // Flip it back to newest to oldest
            return out.reverse();
        },
    );
}

/**
 * @TODO: перенесено из старого кода. Нужно переработать
 */
function makePrepareReplyIdsForThreadViewer() {
    const filterRepliesAndAddSeparators = makeFilterRepliesAndAddSeparators();
    const combineUserActivityPosts = makeCombineUserActivityPosts();

    return (state: GlobalState, options: PostFilterOptions) => {
        const postIds = filterRepliesAndAddSeparators(state, options);
        return combineUserActivityPosts(state, postIds) as string[];
    };
}

const selector = makePrepareReplyIdsForThreadViewer();

const getRepliesListWithSeparators = createSelector(selector, (result) => result, {
    memoizeOptions: {
        resultEqualityCheck: isEqual,
        maxSize: 2,
    },
});

function makeMapStateToProps() {
    return (state: GlobalState, ownProps: OwnProps) => {
        const {postIds, useRelativeTimestamp, selected, channelId} = ownProps;

        const currentUserId = getCurrentUserId(state);
        const isFollowing = getThreadPostFollowState(state, selected.id);
        const lastViewedAtThread = getLastViewedAtByThreadId(state, selected.id) || Date.now();
        const directTeammate = getDirectTeammate(state, channelId);
        const channel = getChannel(state, channelId)!;

        const lastPost = getPost(state, postIds[0]!)!;

        /**
         * Выставляем время последнего просмотра только если следуем за тредом
         */
        const lastViewedAt = isFollowing ? lastViewedAtThread : Date.now();

        const replyListIds = getRepliesListWithSeparators(state, {
            postIds,
            showDate: !useRelativeTimestamp,

            lastViewedAt,
        });

        return {
            currentUserId,
            directTeammate,
            lastPost,
            replyListIds,
            teamId: channel?.team_id,
        };
    };
}

export default connect(makeMapStateToProps)(ThreadViewerVirtualized);
